import { useMemo } from 'react';

import { ActivityIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Skeleton } from '@/components/ui/skeleton';
import type { ContactActivity } from '@/shared/types/contacts';
import { type DateBucket, DATE_BUCKET_ORDER, getDateBucket } from '@/util/dateUtils';

import { ActivityGroup } from './ActivityGroup';
import { ActivityRow } from './ActivityRow';
import { useContact } from '../hooks/useContact';
import { useContactActivities } from '../hooks/useContactActivities';
import { useLiveFollowUpProjections } from '../hooks/useLiveFollowUpProjections';
import { type ActivityItem, groupActivities } from '../utils/groupActivities';

interface ActivityTabProps {
  contactId: string;
  onShowAsOf?: (at: string) => void;
}

const BUCKET_LABEL_KEY: Record<DateBucket, string> = {
  today: 'contacts.activity_today',
  yesterday: 'contacts.activity_yesterday',
  last_7_days: 'contacts.activity_last_7_days',
  last_30_days: 'contacts.activity_last_30_days',
  older: 'contacts.activity_older',
};

const groupByBucket = (activities: ContactActivity[]): Record<DateBucket, ActivityItem[]> => {
  const now = Date.now();
  const buckets: Record<DateBucket, ContactActivity[]> = {
    today: [],
    yesterday: [],
    last_7_days: [],
    last_30_days: [],
    older: [],
  };
  for (const activity of activities) {
    buckets[getDateBucket(activity.timestamp, now)].push(activity);
  }
  return {
    today: groupActivities(buckets.today),
    yesterday: groupActivities(buckets.yesterday),
    last_7_days: groupActivities(buckets.last_7_days),
    last_30_days: groupActivities(buckets.last_30_days),
    older: groupActivities(buckets.older),
  };
};

export const ActivityTab = ({ contactId, onShowAsOf }: ActivityTabProps) => {
  const { activities, status } = useContactActivities(contactId);
  const { contact } = useContact(contactId);
  const liveProjectionIds = useLiveFollowUpProjections(contactId);
  const { t } = useTranslation();

  const buckets = useMemo(() => groupByBucket(activities), [activities]);

  if (status === 'pending') {
    return (
      <div className="space-y-4">
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-12 w-full" />
      </div>
    );
  }

  if (activities.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <div className="mb-4 flex size-12 items-center justify-center rounded-full bg-muted/50">
          <ActivityIcon size={24} className="text-muted-foreground" />
        </div>
        <h3 className="text-sm font-semibold text-foreground">{t('contacts.no_activities')}</h3>
        <p className="mt-1 max-w-sm text-sm text-muted-foreground">
          {t('contacts.no_activities_description')}
        </p>
      </div>
    );
  }

  return (
    <div className="mt-6 flex w-full flex-col px-2">
      {DATE_BUCKET_ORDER.map((bucket) => {
        const rows = buckets[bucket];
        if (rows.length === 0) {
          return null;
        }
        return (
          <section key={bucket} className="flex flex-col gap-3">
            <div className="flex items-center gap-3 py-3">
              <div className="h-px flex-1 bg-border" />
              <span className="text-xs text-muted-foreground">{t(BUCKET_LABEL_KEY[bucket])}</span>
              <div className="h-px flex-1 bg-border" />
            </div>
            <div className="flex flex-col gap-3">
              {rows.map((item) =>
                item.kind === 'group' ? (
                  <ActivityGroup
                    key={item.activities[0].activity_id}
                    activities={item.activities}
                    contactName={contact?.name}
                    onShowAsOf={onShowAsOf}
                  />
                ) : (
                  <ActivityRow
                    key={item.activity.activity_id}
                    activity={item.activity}
                    contactName={contact?.name}
                    showProjection={liveProjectionIds.has(item.activity.activity_id)}
                    onShowAsOf={onShowAsOf}
                  />
                )
              )}
            </div>
          </section>
        );
      })}
    </div>
  );
};
